import React, { useCallback, useEffect, useMemo, useState } from 'react'

import { Button, ButtonTabs, EmptyState } from '@clientbase/clientbase-library'
import {
  Box,
  Chip,
  Skeleton,
  Stack,
  Typography,
  useMediaQuery
} from '@mui/material'
import { ConstructorReceivableParams } from 'models/Receivables'
import moment from 'moment'
import {
  PendingReceivablesFilters,
  PendingReceivablesStatistics,
  ReceivablesService
} from 'services/ReceivablesService'

import Can from 'components/Can'
import InfoList, { Info } from 'components/InfoList/InfoList'
import ModalChargeDetail from 'components/Modal/ModalChargeDetail'
import { Table } from 'components/Table'

import { formatMoney } from 'utils'

import { createDataTable } from '../../../../../ModalBalaceDetail/ModalBalaceDetail.utils'
import * as S from './ListReceivables.styles'

interface ListReceivablesProps {
  onExportReport: () => void
  exportLoading: boolean
  onBackToCalendar: () => void
}

const VIEW_TABS = [
  { label: 'Calendário', value: 'calendar' },
  { label: 'Lista', value: 'list' }
]

type PeriodPreset =
  | 'today'
  | 'this_week'
  | 'next_week'
  | 'this_month'
  | 'next_month'
  | null

const PERIOD_PRESETS: { label: string; value: PeriodPreset }[] = [
  { label: 'Hoje', value: 'today' },
  { label: 'Esta semana', value: 'this_week' },
  { label: 'Próxima semana', value: 'next_week' },
  { label: 'Este mês', value: 'this_month' },
  { label: 'Próximo mês', value: 'next_month' }
]

function getDateRangeFromPreset(
  preset: PeriodPreset
): PendingReceivablesFilters {
  if (!preset) return {}

  const today = moment()

  switch (preset) {
    case 'today':
      return {
        expected_on_from: today.format('YYYY-MM-DD'),
        expected_on_to: today.format('YYYY-MM-DD')
      }
    case 'this_week':
      return {
        expected_on_from: today.clone().startOf('isoWeek').format('YYYY-MM-DD'),
        expected_on_to: today.clone().endOf('isoWeek').format('YYYY-MM-DD')
      }
    case 'next_week':
      return {
        expected_on_from: today
          .clone()
          .add(1, 'week')
          .startOf('isoWeek')
          .format('YYYY-MM-DD'),
        expected_on_to: today
          .clone()
          .add(1, 'week')
          .endOf('isoWeek')
          .format('YYYY-MM-DD')
      }
    case 'this_month':
      return {
        expected_on_from: today.clone().startOf('month').format('YYYY-MM-DD'),
        expected_on_to: today.clone().endOf('month').format('YYYY-MM-DD')
      }
    case 'next_month':
      return {
        expected_on_from: today
          .clone()
          .add(1, 'month')
          .startOf('month')
          .format('YYYY-MM-DD'),
        expected_on_to: today
          .clone()
          .add(1, 'month')
          .endOf('month')
          .format('YYYY-MM-DD')
      }
    default:
      return {}
  }
}

const ListReceivables: React.FC<ListReceivablesProps> = ({
  onExportReport,
  exportLoading,
  onBackToCalendar
}) => {
  const isMobile = useMediaQuery('(max-width: 600px)', { noSsr: false })
  const [loading, setLoading] = useState(true)
  const [records, setRecords] = useState<ConstructorReceivableParams[]>([])
  const [statistics, setStatistics] = useState<PendingReceivablesStatistics>({
    total_net: 0,
    total_gross: 0,
    total_fees: 0,
    count: 0
  })
  const [selectedPeriod, setSelectedPeriod] = useState<PeriodPreset>(null)
  const [tableProps, setTableProps] = useState({
    page: 0,
    rowsPerPage: 25,
    totalCount: 0
  })

  const [modal, setModal] = useState({
    isOpenDetailInvoice: false,
    uuid: ''
  })

  const filters = useMemo(
    () => getDateRangeFromPreset(selectedPeriod),
    [selectedPeriod]
  )

  const handleOpenDetailInvoice = (uuid: string) => {
    setModal({ isOpenDetailInvoice: true, uuid })
  }

  const handleCloseDetailInvoice = () => {
    setModal({ isOpenDetailInvoice: false, uuid: '' })
  }

  const fetchReceivables = useCallback(
    async (
      page: number,
      per: number,
      dateFilters: PendingReceivablesFilters
    ) => {
      try {
        setLoading(true)
        const { records, meta, statistics } =
          await ReceivablesService.getPendingReceivables(
            page + 1,
            per,
            dateFilters
          )

        setRecords(records)
        setStatistics(statistics)
        setTableProps((prev) => ({
          ...prev,
          totalCount: meta.total_count
        }))
      } catch {
        setRecords([])
      } finally {
        setLoading(false)
      }
    },
    []
  )

  useEffect(() => {
    fetchReceivables(tableProps.page, tableProps.rowsPerPage, filters)
  }, [tableProps.page, tableProps.rowsPerPage, filters, fetchReceivables])

  const handleChangePage = (
    _: React.MouseEvent<HTMLButtonElement, MouseEvent> | null,
    page: number
  ) => {
    setTableProps((prev) => ({ ...prev, page }))
  }

  const handleChangeRowsPerPage = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const rowsPerPage = Number(e.target.value || 25)
    setTableProps((prev) => ({ ...prev, rowsPerPage, page: 0 }))
  }

  const handlePeriodChange = (preset: PeriodPreset) => {
    setSelectedPeriod((prev) => (prev === preset ? null : preset))
    setTableProps((prev) => ({ ...prev, page: 0 }))
  }

  const infos: Info[] = [
    {
      title: 'Total a liberar',
      subtitle: formatMoney(statistics.total_net)
    },
    {
      title: 'Quantidade',
      subtitle: String(statistics.count)
    }
  ]

  const rows = records.map((record) => {
    const { uuid, ...row } = createDataTable({
      uuid: record.billing.uuid,
      name: record.billing.customer.name,
      nickname: record.billing.customer?.nickname || '',
      dueDate: record.billing.due_date,
      paidDate: record.billing.date_paid,
      amountPaid: record.gross_amount,
      amount: record.amount,
      payments: record.billing.payments,
      handleOpenDetailInvoice,
      installment: record.installment
    })

    return {
      uuid,
      releaseDate: record.expected_on
        ? moment(record.expected_on).format('DD/MM/YYYY')
        : '-',
      ...row
    }
  })

  return (
    <S.Wrapper>
      <S.ToolbarContainer>
        <Can permission="reports:export">
          <Button
            variantButton="outlined"
            aria-label="Exportar relatório"
            disabled={exportLoading}
            onClick={onExportReport}
          >
            Exportar Relatório
          </Button>
        </Can>

        <ButtonTabs
          value="list"
          tabs={VIEW_TABS}
          onChange={(_, value) => {
            if (value === 'calendar') {
              onBackToCalendar()
            }
          }}
        />
      </S.ToolbarContainer>

      <Box mb={2}>
        {loading ? (
          <Stack
            direction="row"
            sx={{
              border: (t) => `1px solid ${t.palette.neutral[700]}`,
              borderRadius: '12px',
              overflow: 'hidden'
            }}
          >
            {[0, 1].map((i) => (
              <Box
                key={i}
                sx={{
                  flex: 1,
                  p: '20px 27px',
                  borderLeft: (t) =>
                    i === 0 ? 'none' : `1px solid ${t.palette.neutral[700]}`
                }}
              >
                <Skeleton
                  variant="text"
                  width="70%"
                  height={18}
                  animation="wave"
                />
                <Skeleton
                  variant="text"
                  width="85%"
                  height={24}
                  animation="wave"
                />
              </Box>
            ))}
          </Stack>
        ) : isMobile ? (
          // O InfoList empilha em 1 coluna abaixo de 600px e comia ~160px de
          // altura: junto com os chips nao sobrava espaco pra tabela no modal.
          <Stack
            direction="row"
            alignItems="baseline"
            flexWrap="wrap"
            columnGap={1}
            sx={{
              border: (t) => `1px solid ${t.palette.neutral[700]}`,
              borderRadius: '12px',
              padding: '10px 14px'
            }}
          >
            <Typography variant="xsLight" color="neutral.300">
              Total a liberar
            </Typography>
            <Typography variant="smBold">
              {formatMoney(statistics.total_net)}
            </Typography>
            <Typography variant="xsLight" color="neutral.300">
              · {statistics.count}{' '}
              {statistics.count === 1 ? 'recebível' : 'recebíveis'}
            </Typography>
          </Stack>
        ) : (
          <InfoList infos={infos} disableBoxShadow />
        )}
      </Box>

      <Stack
        direction="row"
        gap={1}
        flexWrap={isMobile ? 'nowrap' : 'wrap'}
        mb={2}
        // no mobile os 5 chips quebravam em 2 linhas; viram faixa rolavel
        sx={
          isMobile
            ? {
                overflowX: 'auto',
                pb: '4px',
                '&::-webkit-scrollbar': { height: 4 }
              }
            : undefined
        }
      >
        {PERIOD_PRESETS.map(({ label, value }) => (
          <Chip
            key={value}
            label={label}
            variant={selectedPeriod === value ? 'filled' : 'outlined'}
            color={selectedPeriod === value ? 'primary' : 'default'}
            onClick={() => handlePeriodChange(value)}
            sx={isMobile ? { flexShrink: 0 } : undefined}
          />
        ))}
      </Stack>

      {loading ? (
        <Skeleton height="400px" variant="rectangular" width="100%" />
      ) : !records.length ? (
        <S.EmptyWrapper>
          <Box maxWidth="600px" width="100%">
            <EmptyState
              title="Nenhum registro encontrado"
              message="Não há recebíveis agendados para liberação."
            />
          </Box>
        </S.EmptyWrapper>
      ) : (
        <S.TableWrapper>
          <Table
            mobileCards
            header={[
              { id: 'releaseDate', label: 'Data de Liberação' },
              { id: 'name', label: 'Cliente' },
              { id: 'dueDate', label: 'Data de Vencimento' },
              { id: 'paidDate', label: 'Data Pagamento' },
              { id: 'amountPaid', label: 'Valor Pago' },
              { id: 'fee', label: 'Tarifa bancária' },
              { id: 'amountLiquid', label: 'Valor Líquido' },
              { id: 'paymentType', label: 'Método de Pagamento' },
              { id: 'actions', label: '' }
            ]}
            handleChangePage={handleChangePage}
            handleChangeRowsPerPage={handleChangeRowsPerPage}
            rows={rows}
            rowAction={handleOpenDetailInvoice}
            select={{ useSelect: false }}
            hoverInRow
            {...tableProps}
          />
        </S.TableWrapper>
      )}

      <ModalChargeDetail
        handleCloseChargeDetail={handleCloseDetailInvoice}
        isOpenChargeDetail={modal.isOpenDetailInvoice}
        uuid={modal.uuid}
        disablePortal={false}
      />
    </S.Wrapper>
  )
}

export default ListReceivables
